import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import VerticalLayout from 'components/Layouts/VerticalLayout'
import EmailVerifyGuard from './email-verify-guard'
import { useRoleRouteGuard } from './use-role-route-guard'

/**
 * Authed shell for the flat cross-team layout.
 *
 * Responsibilities lifted out of the legacy SubDomainRoutes:
 * - Forwards FCM/service-worker notification clicks into in-app navigation so a
 *   click on a push notification deep-links instead of cold-loading.
 *
 * Email-verify gating is delegated to EmailVerifyGuard so identity-bootstrap
 * routes (/select-organization, /select-profile) can compose the same check
 * without going through the layout.
 *
 * Cross-team migration: the multi-org picker gate is retired here. Pages
 * scope themselves via `useTeamFilter` (lists) or `<TeamPickerGate>` (settings),
 * so the global picker bounce no longer adds value.
 */
const AuthedShell = observer(() => {
  const navigate = useNavigate()

  // Redirect to /dashboard when the current route is role-gated beyond the user's
  // cross-team role (e.g. after being removed from a team and refreshing on a
  // coach-only page like /groups).
  useRoleRouteGuard()

  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const { targetUrl } = event.data
        navigate(targetUrl)
      }
    }
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage)
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage)
    }
  }, [navigate])

  // Cross-team RallyPD chrome: dark PlayTopbar + dark LeftSidebar on every
  // authed route. Page body stays the default white surface until each page
  // opts into its own dark surface.
  return (
    <EmailVerifyGuard>
      <VerticalLayout usePlayTopbar />
    </EmailVerifyGuard>
  )
})

export default AuthedShell
